import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CanvasElement, ElementType, Point } from '@extapia/shared';
import { boundsOf, mergeBounds } from '@extapia/shared';
import {
  createRoughGen,
  hitTestSelection,
  renderScene,
  screenToWorld,
  type Viewport,
} from '@extapia/canvas';
import { createElement, useCanvas } from '../state/canvas-store.js';
import { saveLocal } from '../lib/persistence.js';
import { applyResize, handleSize, handlesForSelection, hitHandle, type HandleKind } from '../canvas/handles.js';

type PointerState =
  | { kind: 'idle' }
  | { kind: 'drawing'; elementId: string; startWorld: Point }
  | { kind: 'freehand'; elementId: string; startWorld: Point }
  | {
      kind: 'dragging';
      startWorld: Point;
      ids: string[];
      origPositions: Map<string, Point>;
    }
  | { kind: 'panning'; startScreen: Point; startScroll: Point }
  | { kind: 'rubberband'; startWorld: Point; endWorld: Point }
  | {
      kind: 'resizing';
      startWorld: Point;
      handle: HandleKind;
      origElement: CanvasElement;
      elementId: string;
    }
  | {
      kind: 'rotating';
      startWorld: Point;
      centerWorld: Point;
      origAngle: number;
      ids: string[];
    }
  | { kind: 'erasing' };

const isRectTool = (t: ElementType | 'selection' | 'hand' | 'eraser'): t is ElementType =>
  t === 'rect' ||
  t === 'ellipse' ||
  t === 'diamond' ||
  t === 'sticky' ||
  t === 'text' ||
  t === 'frame';
const isLineTool = (t: ElementType | 'selection' | 'hand' | 'eraser'): t is ElementType =>
  t === 'line' || t === 'arrow';

const snapToGrid = (v: number, size: number | null): number =>
  size ? Math.round(v / size) * size : v;

export const Canvas = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const roughRef = useRef<ReturnType<typeof createRoughGen> | null>(null);
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const pointerRef = useRef<PointerState>({ kind: 'idle' });
  const [hoverCursor, setHoverCursor] = useState<string>('default');
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

  const elements = useCanvas((s) => s.elements);
  const selectedIds = useCanvas((s) => s.selectedIds);
  const app = useCanvas((s) => s.app);
  const addElement = useCanvas((s) => s.addElement);
  const updateElement = useCanvas((s) => s.updateElement);
  const selectOnly = useCanvas((s) => s.selectOnly);
  const clearSelection = useCanvas((s) => s.clearSelection);
  const setViewport = useCanvas((s) => s.setViewport);
  const commit = useCanvas((s) => s.commit);
  const setTool = useCanvas((s) => s.setTool);
  const startTextEdit = useCanvas((s) => s.startTextEdit);
  const deleteElement = useCanvas((s) => s.deleteElement);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0]!.contentRect;
      setSize({ w: rect.width, h: rect.height });
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (canvasRef.current) roughRef.current = createRoughGen(canvasRef.current);
  }, []);

  // Render loop
  useEffect(() => {
    const canvas = canvasRef.current;
    const rc = roughRef.current;
    if (!canvas || !rc || size.w === 0) return;

    canvas.width = size.w * dpr;
    canvas.height = size.h * dpr;
    canvas.style.width = `${size.w}px`;
    canvas.style.height = `${size.h}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const viewport: Viewport = { scrollX: app.scrollX, scrollY: app.scrollY, zoom: app.zoom };
    const visible = elements.filter((e) => !e.isDeleted);

    renderScene(
      {
        ctx,
        rc,
        width: size.w,
        height: size.h,
        dpr,
        viewport,
        background: app.viewBackgroundColor,
        gridSize: app.gridSize,
        theme: app.theme,
      },
      visible,
    );

    // Selection overlay + handles
    if (selectedIds.size > 0) {
      const selected = visible.filter((e) => selectedIds.has(e.id));
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.scale(viewport.zoom, viewport.zoom);
      ctx.translate(-viewport.scrollX, -viewport.scrollY);

      // per-element dashed outline
      ctx.strokeStyle = '#0ea5e9';
      ctx.lineWidth = 1 / viewport.zoom;
      ctx.setLineDash([6 / viewport.zoom, 4 / viewport.zoom]);
      for (const el of selected) {
        const b = boundsOf(el);
        ctx.strokeRect(b.x1 - 2, b.y1 - 2, b.x2 - b.x1 + 4, b.y2 - b.y1 + 4);
      }
      ctx.setLineDash([]);

      // group bounding + handles
      const merged = mergeBounds(selected.map(boundsOf));
      if (merged) {
        ctx.strokeStyle = '#0ea5e9';
        ctx.strokeRect(merged.x1 - 6, merged.y1 - 6, merged.x2 - merged.x1 + 12, merged.y2 - merged.y1 + 12);

        const handles = handlesForSelection(selected);
        const hs = handleSize(viewport.zoom);
        ctx.fillStyle = '#ffffff';
        ctx.strokeStyle = '#0ea5e9';
        ctx.lineWidth = 1.5 / viewport.zoom;
        for (const h of handles) {
          if (h.kind === 'rotate') {
            // circle
            ctx.beginPath();
            ctx.arc(h.x, h.y, hs, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
            // link line
            ctx.beginPath();
            ctx.moveTo(h.x, h.y + hs);
            ctx.lineTo(h.x, merged.y1 - 6);
            ctx.stroke();
          } else {
            ctx.fillRect(h.x - hs, h.y - hs, hs * 2, hs * 2);
            ctx.strokeRect(h.x - hs, h.y - hs, hs * 2, hs * 2);
          }
        }
      }
      ctx.restore();
    }

    // Rubber-band overlay
    const p = pointerRef.current;
    if (p.kind === 'rubberband') {
      const { startWorld, endWorld } = p;
      const x1 = Math.min(startWorld.x, endWorld.x);
      const y1 = Math.min(startWorld.y, endWorld.y);
      const x2 = Math.max(startWorld.x, endWorld.x);
      const y2 = Math.max(startWorld.y, endWorld.y);
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.scale(viewport.zoom, viewport.zoom);
      ctx.translate(-viewport.scrollX, -viewport.scrollY);
      ctx.fillStyle = 'rgba(14, 165, 233, 0.08)';
      ctx.strokeStyle = '#0ea5e9';
      ctx.lineWidth = 1 / viewport.zoom;
      ctx.setLineDash([4 / viewport.zoom, 4 / viewport.zoom]);
      ctx.fillRect(x1, y1, x2 - x1, y2 - y1);
      ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
      ctx.restore();
    }
  }, [elements, selectedIds, app, size, dpr]);

  // Autosave
  useEffect(() => {
    const t = setTimeout(() => {
      saveLocal({ elements: elements.filter((e) => !e.isDeleted), appState: app });
    }, 400);
    return () => clearTimeout(t);
  }, [elements, app]);

  const worldFromEvent = useCallback(
    (ev: { clientX: number; clientY: number }): Point => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };
      return screenToWorld(
        { x: ev.clientX - rect.left, y: ev.clientY - rect.top },
        { scrollX: app.scrollX, scrollY: app.scrollY, zoom: app.zoom },
      );
    },
    [app.scrollX, app.scrollY, app.zoom],
  );

  const selectedElements = useMemo(
    () => elements.filter((e) => selectedIds.has(e.id) && !e.isDeleted),
    [elements, selectedIds],
  );

  const onPointerDown = useCallback(
    (ev: React.PointerEvent<HTMLCanvasElement>) => {
      const target = ev.currentTarget;
      target.setPointerCapture(ev.pointerId);
      const world = worldFromEvent(ev);
      const tool = app.currentTool;

      if (ev.button === 1 || tool === 'hand' || (ev.button === 0 && ev.altKey)) {
        pointerRef.current = {
          kind: 'panning',
          startScreen: { x: ev.clientX, y: ev.clientY },
          startScroll: { x: app.scrollX, y: app.scrollY },
        };
        return;
      }

      if (tool === 'eraser') {
        pointerRef.current = { kind: 'erasing' };
        // start immediate erase
        const hit = hitTestSelection(world, elements);
        if (hit) deleteElement(hit.id);
        return;
      }

      // Handle hit-test for resize/rotate
      if (tool === 'selection' && selectedElements.length > 0) {
        const handles = handlesForSelection(selectedElements);
        const hh = hitHandle(world, handles, app.zoom);
        if (hh) {
          if (hh.kind === 'rotate') {
            const merged = mergeBounds(selectedElements.map(boundsOf));
            if (merged) {
              const cx = (merged.x1 + merged.x2) / 2;
              const cy = (merged.y1 + merged.y2) / 2;
              pointerRef.current = {
                kind: 'rotating',
                startWorld: world,
                centerWorld: { x: cx, y: cy },
                origAngle: selectedElements[0]!.angle,
                ids: selectedElements.map((e) => e.id),
              };
              return;
            }
          } else if (selectedElements.length === 1) {
            const el = selectedElements[0]!;
            pointerRef.current = {
              kind: 'resizing',
              startWorld: world,
              handle: hh.kind,
              origElement: { ...el },
              elementId: el.id,
            };
            return;
          }
        }
      }

      if (tool === 'selection') {
        const hit = hitTestSelection(world, elements);
        if (hit) {
          if (!selectedIds.has(hit.id) && !ev.shiftKey) selectOnly([hit.id]);
          else if (ev.shiftKey) {
            const next = new Set(selectedIds);
            if (next.has(hit.id)) next.delete(hit.id);
            else next.add(hit.id);
            selectOnly([...next]);
          }
          const ids = ev.shiftKey
            ? [...selectedIds, hit.id]
            : selectedIds.has(hit.id)
            ? [...selectedIds]
            : [hit.id];
          const origPositions = new Map<string, Point>();
          for (const el of elements) if (ids.includes(el.id)) origPositions.set(el.id, { x: el.x, y: el.y });
          pointerRef.current = { kind: 'dragging', startWorld: world, ids, origPositions };
        } else {
          if (!ev.shiftKey) clearSelection();
          pointerRef.current = { kind: 'rubberband', startWorld: world, endWorld: world };
        }
        return;
      }

      if (tool === 'freehand') {
        const el = createElement({
          type: 'freehand',
          x: world.x,
          y: world.y,
          width: 0,
          height: 0,
          app,
          elementsForIndex: elements,
        });
        if (el.type === 'freehand') el.points = [[0, 0, ev.pressure || 0.5]];
        addElement(el);
        pointerRef.current = { kind: 'freehand', elementId: el.id, startWorld: world };
        return;
      }

      if (tool === 'text') {
        // Create text element with default size, immediately open editor.
        const el = createElement({
          type: 'text',
          x: snapToGrid(world.x, app.gridSize),
          y: snapToGrid(world.y, app.gridSize),
          width: 100,
          height: app.activeFontSize * 1.25,
          app,
          elementsForIndex: elements,
        });
        addElement(el);
        setTool('selection');
        startTextEdit(el.id);
        pointerRef.current = { kind: 'idle' };
        return;
      }

      if (isRectTool(tool) || isLineTool(tool)) {
        const el = createElement({
          type: tool,
          x: snapToGrid(world.x, app.gridSize),
          y: snapToGrid(world.y, app.gridSize),
          width: 0,
          height: 0,
          app,
          elementsForIndex: elements,
        });
        addElement(el);
        pointerRef.current = { kind: 'drawing', elementId: el.id, startWorld: world };
      }
    },
    [
      app,
      elements,
      selectedIds,
      selectedElements,
      addElement,
      selectOnly,
      clearSelection,
      worldFromEvent,
      setTool,
      startTextEdit,
      deleteElement,
    ],
  );

  const onPointerMove = useCallback(
    (ev: React.PointerEvent<HTMLCanvasElement>) => {
      const st = pointerRef.current;
      const world = worldFromEvent(ev);

      // Idle hover — update cursor based on handles
      if (st.kind === 'idle' && app.currentTool === 'selection' && selectedElements.length > 0) {
        const handles = handlesForSelection(selectedElements);
        const hh = hitHandle(world, handles, app.zoom);
        setHoverCursor(hh ? hh.cursor : 'default');
      }

      if (st.kind === 'idle') return;

      switch (st.kind) {
        case 'panning': {
          const dx = ev.clientX - st.startScreen.x;
          const dy = ev.clientY - st.startScreen.y;
          setViewport({
            scrollX: st.startScroll.x - dx / app.zoom,
            scrollY: st.startScroll.y - dy / app.zoom,
          });
          break;
        }
        case 'drawing': {
          const w = world.x - st.startWorld.x;
          const h = world.y - st.startWorld.y;
          const patch: Partial<CanvasElement> = {
            x: snapToGrid(w < 0 ? world.x : st.startWorld.x, app.gridSize),
            y: snapToGrid(h < 0 ? world.y : st.startWorld.y, app.gridSize),
            width: Math.abs(w),
            height: Math.abs(h),
          };
          if (isLineTool(app.currentTool)) {
            (patch as { points?: Array<[number, number]> }).points = [
              [0, 0],
              [world.x - st.startWorld.x, world.y - st.startWorld.y],
            ];
            patch.x = st.startWorld.x;
            patch.y = st.startWorld.y;
            patch.width = Math.abs(w);
            patch.height = Math.abs(h);
          }
          updateElement(st.elementId, patch);
          break;
        }
        case 'freehand': {
          const el = elements.find((e) => e.id === st.elementId);
          if (!el || el.type !== 'freehand') break;
          const nextPoints: Array<[number, number, number?]> = [
            ...el.points,
            [world.x - st.startWorld.x, world.y - st.startWorld.y, ev.pressure || 0.5],
          ];
          const xs = nextPoints.map((p) => p[0]);
          const ys = nextPoints.map((p) => p[1]);
          const minX = Math.min(...xs);
          const minY = Math.min(...ys);
          updateElement(st.elementId, {
            points: nextPoints,
            width: Math.max(...xs) - minX,
            height: Math.max(...ys) - minY,
          });
          break;
        }
        case 'dragging': {
          const dx = world.x - st.startWorld.x;
          const dy = world.y - st.startWorld.y;
          for (const id of st.ids) {
            const orig = st.origPositions.get(id);
            if (!orig) continue;
            updateElement(id, {
              x: snapToGrid(orig.x + dx, app.gridSize),
              y: snapToGrid(orig.y + dy, app.gridSize),
            });
          }
          break;
        }
        case 'rubberband': {
          pointerRef.current = { ...st, endWorld: world };
          setViewport({});
          break;
        }
        case 'resizing': {
          const dx = world.x - st.startWorld.x;
          const dy = world.y - st.startWorld.y;
          const next = applyResize(
            {
              x: st.origElement.x,
              y: st.origElement.y,
              width: st.origElement.width,
              height: st.origElement.height,
            },
            st.handle,
            dx,
            dy,
            ev.shiftKey,
          );
          updateElement(st.elementId, next);
          break;
        }
        case 'rotating': {
          const dx = world.x - st.centerWorld.x;
          const dy = world.y - st.centerWorld.y;
          const angle = Math.atan2(dy, dx) + Math.PI / 2;
          for (const id of st.ids) updateElement(id, { angle });
          break;
        }
        case 'erasing': {
          const hit = hitTestSelection(world, elements);
          if (hit) deleteElement(hit.id);
          break;
        }
      }
    },
    [
      app,
      elements,
      selectedElements,
      updateElement,
      setViewport,
      worldFromEvent,
      deleteElement,
    ],
  );

  const onPointerUp = useCallback(() => {
    const st = pointerRef.current;
    if (st.kind === 'rubberband') {
      const x1 = Math.min(st.startWorld.x, st.endWorld.x);
      const y1 = Math.min(st.startWorld.y, st.endWorld.y);
      const x2 = Math.max(st.startWorld.x, st.endWorld.x);
      const y2 = Math.max(st.startWorld.y, st.endWorld.y);
      const inside = elements
        .filter((e) => !e.isDeleted)
        .filter((e) => {
          const b = boundsOf(e);
          return b.x1 >= x1 && b.y1 >= y1 && b.x2 <= x2 && b.y2 <= y2;
        })
        .map((e) => e.id);
      if (inside.length > 0) selectOnly(inside);
    }

    if (
      st.kind === 'drawing' ||
      st.kind === 'freehand' ||
      st.kind === 'dragging' ||
      st.kind === 'resizing' ||
      st.kind === 'rotating' ||
      st.kind === 'erasing'
    ) {
      commit();
    }
    pointerRef.current = { kind: 'idle' };
    if (st.kind === 'drawing' || st.kind === 'freehand') setTool('selection');
  }, [elements, selectOnly, commit, setTool]);

  // Double-click on text element → open editor
  const onDoubleClick = useCallback(
    (ev: React.MouseEvent<HTMLCanvasElement>) => {
      const world = worldFromEvent(ev);
      const hit = hitTestSelection(world, elements);
      if (hit && hit.type === 'text') {
        startTextEdit(hit.id);
      }
    },
    [elements, worldFromEvent, startTextEdit],
  );

  const onWheel = useCallback(
    (ev: React.WheelEvent<HTMLCanvasElement>) => {
      if (ev.ctrlKey || ev.metaKey) {
        const rect = canvasRef.current?.getBoundingClientRect();
        if (!rect) return;
        const screenX = ev.clientX - rect.left;
        const screenY = ev.clientY - rect.top;
        const worldBefore = screenToWorld(
          { x: screenX, y: screenY },
          { scrollX: app.scrollX, scrollY: app.scrollY, zoom: app.zoom },
        );
        const factor = Math.exp(-ev.deltaY * 0.002);
        const zoom = Math.max(0.1, Math.min(30, app.zoom * factor));
        setViewport({ zoom, scrollX: worldBefore.x - screenX / zoom, scrollY: worldBefore.y - screenY / zoom });
      } else {
        setViewport({
          scrollX: app.scrollX + ev.deltaX / app.zoom,
          scrollY: app.scrollY + ev.deltaY / app.zoom,
        });
      }
    },
    [app.scrollX, app.scrollY, app.zoom, setViewport],
  );

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => e.preventDefault();
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, []);

  const cursor = useMemo(() => {
    if (app.currentTool === 'hand') return 'grab';
    if (app.currentTool === 'eraser') return 'crosshair';
    if (app.currentTool === 'selection') return hoverCursor;
    return 'crosshair';
  }, [app.currentTool, hoverCursor]);

  return (
    <div ref={containerRef} className="absolute inset-0">
      <canvas
        ref={canvasRef}
        style={{ cursor }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={onDoubleClick}
        onWheel={onWheel}
        onContextMenu={(e) => e.preventDefault()}
      />
    </div>
  );
};
